import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Sidebar from './docs/Sidebar';
import ContentRenderer from './docs/ContentRenderer';
import TableOfContents from './docs/TableOfContents';

const Docs = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Handle URL hash changes
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1); // Remove the # symbol
      if (hash) {
        setActiveSection(hash);
      }
    };

    // Set initial section from URL hash
    handleHashChange();

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  const handleMenuItemClick = (sectionId) => {
    setActiveSection(sectionId);
    setIsMobileMenuOpen(false); // Close mobile menu when item is selected

    // Update URL hash
    window.history.pushState(null, null, `#${sectionId}`);
  };

  // API Reference pages that should not show table of contents
  const apiReferencePages = [
    // 'responseapi',
    // 'response-api-js',
    // 'memory'
    // 'memory-apis',
    // 'memorize-conversation'
  ];

  const shouldShowTOC = !apiReferencePages.includes(activeSection);

  return (
    <>
      <Helmet>
        <title>MemU Documentation - Complete Guide for AI Memory Integration</title>
        <meta name="description" content="Complete documentation for MemU - Learn how to integrate AI memory layer into your LLM applications. Quickstart guide, API reference, use cases, and best practices." />
        <meta name="keywords" content="memu documentation, ai memory guide, llm memory integration, ai application tutorial, memory layer docs, api reference, quickstart guide" />
        
        {/* Open Graph */}
        <meta property="og:title" content="MemU Documentation - Complete Guide for AI Memory Integration" />
        <meta property="og:description" content="Complete documentation for MemU - Learn how to integrate AI memory layer into your LLM applications. Quickstart guide, API reference, use cases, and best practices." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://memu.pro/docs" />
        <meta property="og:image" content="https://d192tm8h0ep0ud.cloudfront.net/memu.pro/static/memu-logo.png" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="MemU Documentation - Complete Guide for AI Memory Integration" />
        <meta name="twitter:description" content="Complete documentation for MemU - Learn how to integrate AI memory layer into your LLM applications. Quickstart guide, API reference, use cases, and best practices." />
        <meta name="twitter:image" content="https://d192tm8h0ep0ud.cloudfront.net/memu.pro/static/memu-logo.png" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "TechArticle",
            "headline": "MemU Documentation",
            "description": "Complete documentation for integrating AI memory layer into LLM applications",
            "url": "https://memu.pro/docs",
            "author": {
              "@type": "Organization",
              "name": "MemU Team"
            },
            "publisher": {
              "@type": "Organization", 
              "name": "MemU",
              "logo": {
                "@type": "ImageObject",
                "url": "https://d192tm8h0ep0ud.cloudfront.net/memu.pro/static/logo.png"
              }
            },
            "about": {
              "@type": "SoftwareApplication",
              "name": "MemU",
              "description": "AI memory layer for LLM applications"
            }
          })}
        </script>
      </Helmet>
      
      <div className="docs-fullscreen">
      {/* Mobile Menu Toggle Button */}
      <button 
        className="mobile-menu-toggle"
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        aria-label="Toggle navigation menu"
      >
        <i className={`fa-solid ${isMobileMenuOpen ? 'fa-times' : 'fa-bars'}`}></i>
      </button>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="mobile-menu-overlay"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <div className="docs-layout">
        <Sidebar 
          activeSection={activeSection}
          onSectionChange={handleMenuItemClick}
          isMobileMenuOpen={isMobileMenuOpen}
        />
        
        <main className="docs-main">
          <ContentRenderer activeSection={activeSection} onSectionChange={handleMenuItemClick} />
        </main>
        
        {shouldShowTOC && <TableOfContents activeSection={activeSection} />}
      </div>
    </div>
    </>
  );
};

export default Docs;